<script lang="ts" setup>
import { defineEmits, defineProps, getCurrentInstance, ref, computed, onMounted } from 'vue'
import { VueEditor } from 'vue2-editor'
import BKEditorConfig from '@/common/vue2Config'
import { Studio } from '@/modules/Studios/types'
import GmapsPlaceInput from '@/components/GmapsPlaceInput.vue'
import { useImage } from '@/shared/composables/image'

const instance = getCurrentInstance()
const { saveImage, getStudioImage } = useImage()

const props = defineProps<{
  studio: Studio
  isLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'update'): void
}>()

const mask = ref('##/##/####')
const editorConfig = ref(BKEditorConfig)
const studioImage = ref('')
const studioImageFile = ref<FileList | null>(null)

const changeImage = (e: Event) => {
  const target = e.target as HTMLInputElement
  const filesImg = target.files

  if (!filesImg || filesImg.length === 0) return

  const reader = new FileReader()
  reader.onload = (event) => {
    studioImage.value = event.target?.result as string
    studioImageFile.value = filesImg
  }
  reader.readAsDataURL(filesImg[0])
}

// Computed property for location data
const locationData = computed({
  get: () => ({
    address: props.studio.locationAddress || '',
    lat: props.studio.locationLat || 0,
    lng: props.studio.locationLng || 0,
  }),
  set: (value: { address: string; lat: number; lng: number }) => {
    props.studio.locationAddress = value.address
    props.studio.locationLat = value.lat
    props.studio.locationLng = value.lng
  },
})

async function updateGeneralInfo() {
  console.log(props.studio)
  if (props.studio.description) {
    props.studio.description = instance?.proxy?.$sanitize(props.studio.description)
  }

  if (studioImageFile.value) {
    props.studio.image = await saveImage(studioImageFile.value[0])
  }

  emit('update')
}

onMounted(() => {
  studioImage.value = getStudioImage(props.studio.image, 72)
})
</script>

<template>
  <div class="settings-general">
    <div class="fields">
      <div class="field image-field">
        <v-img
          :src="studioImage"
          class="studio-image"
          height="72"
          width="72"
        />
        <label
          :class="['label', { 'label-edit': studioImageFile || studio.image }]"
          for="studioImageInput"
        >
          <img
            src="/img/ic_Image.svg"
            alt="edit image icon"
            class="label-icon"
          />
          <span class="label-text">{{ studioImageFile || studio.image ? 'Edit Image' : 'Upload Image' }}</span>
          <input
            id="studioImageInput"
            accept="image/png, image/jpeg"
            name="studioImage"
            style="display: none"
            type="file"
            @change="changeImage"
          />
        </label>
      </div>

      <div class="field">
        <v-text-field
          v-model="studio.name"
          hide-details
          label="Studio name"
          outlined
        />
      </div>

      <div class="field">
        <v-text-field
          v-model="studio.founded"
          v-mask="mask"
          hide-details
          label="Date created"
          outlined
        />
      </div>

      <div class="field">
        <GmapsPlaceInput
          v-model="locationData"
          placeholder="Location address"
        />
      </div>

      <div class="field">
        <VueEditor
          v-model="studio.description"
          :editor-toolbar="editorConfig"
          label="Description"
        />
      </div>
    </div>

    <div class="options">
      <v-btn
        :loading="isLoading"
        color="var(--color-blue-800)"
        dark
        height="52"
        width="180"
        @click="updateGeneralInfo"
      >
        Save
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.settings-general {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
}

.fields {
  padding: 20px;
  flex: 1;
  overflow-y: auto;

  .field {
    margin-bottom: 16px;
  }

  ::v-deep .ql-container {
    max-height: 275px;
    overflow: auto;
  }

  .image-field {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
  }

  .studio-image {
    flex: 0 0 72px;
    border-radius: 4px;
    background-color: var(--color-grey-600);
    overflow: hidden;
  }

  .label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: fit-content;
    padding: 8px 12px;
    background-color: var(--color-white);
    cursor: pointer;
    border: 1px solid var(--color-blue-800);
    border-radius: 4px;
    transition: opacity 0.2s ease-in-out;
    font-size: 12px;

    .label-text {
      font-family: ff-good-web-pro, Roboto, Helvetica, sans-serif;
      font-weight: 700;
      white-space: nowrap;
    }

    .label-icon {
      width: 16px;
      height: 16px;
      object-fit: contain;
    }

    &:hover,
    &:focus {
      opacity: 0.85;
    }
  }

  .label-edit {
    font-weight: bold;
  }
}

.options {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding: 20px;
  border-top: 1px solid var(--color-grey-600);
}
</style>
